// ============================================================================
// SUPABASE CLIENT - ADMIN APP
// ============================================================================

import { createClient } from '@supabase/supabase-js';
import { patchSessionGuard } from '@socialflows/shared-lib';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

patchSessionGuard(supabase);

// Get current session
export async function getSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  return { session, error };
}

// Get current user
export async function getUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  return { user, error };
}

export async function signIn(email: string, password: string) {
  const { data, error: fnError } = await supabase.functions.invoke('login', {
    body: { email, password },
  });

  if (fnError) {
    return { data: { user: null, session: null }, error: fnError };
  }

  if (data?.error) {
    return { data: { user: null, session: null }, error: new Error(data.error) };
  }

  if (!data?.session) {
    return { data: { user: null, session: null }, error: new Error('No session returned') };
  }

  const { error: setError } = await supabase.auth.setSession({
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token,
  });

  if (setError) {
    return { data: { user: null, session: null }, error: setError };
  }

  return {
    data: { user: data.session.user, session: data.session },
    error: null,
  };
}

// Sign out
export async function signOut() {
  return await supabase.auth.signOut();
}

// Get user profile from users table
export async function getUserProfile(userId: string) {
  const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();

  return { data, error };
}

// Get account by hostname (for multi-tenancy)
export async function getAccountByHostname(hostname: string) {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .eq('hostname', hostname)
    .maybeSingle();

  return { data, error };
}

// Real-time subscription helper
export function subscribeToTable(
  table: string,
  callback: (payload: any) => void,
  filter?: { column: string; value: any }
) {
  const channelName = filter ? `${table}-${filter.column}-${filter.value}` : `${table}-changes`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: table,
        filter: filter ? `${filter.column}=eq.${filter.value}` : undefined,
      },
      callback
    )
    .subscribe();

  return channel;
}

// Export default client
export default supabase;
